import React, { useState, useEffect } from "react";
import { Grid, Paper, Typography, Button, Box } from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Container, Row, Col } from "react-bootstrap";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { API_URL } from "../../utils/config";
const QueryManage = () => {
  const [queries, setQueries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [formData, setFormData] = useState({
    status: "",
    response: "",
    resolvedBy: localStorage.getItem("userId"),
  });
  const [statusFilter, setStatusFilter] = useState("open");

  const fetchQueries = async () => {
    setIsLoading(true);
    try {
      // Admin endpoint to get all queries
      const response = await fetch(
        `${API_URL}/admin/query-master`,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      if (response.status === 401) {
        localStorage.clear();
        window.location.reload();
        return;
      }

      const data = await response.json();
      setQueries(data);
    } catch (error) {
      console.error("Error fetching queries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQueries();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleEdit = (query) => {
    setSelectedQuery(query);
    setFormData({
      status: query.status,
      response: query.response || "",
      resolvedBy: localStorage.getItem("userId"),
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this query?"
    );
    if (!isConfirmed) return;

    try {
      const response = await fetch(
        `${API_URL}/admin/query-master/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to delete query");
      }
      fetchQueries();
      alert("Query deleted successfully");
    } catch (error) {
      console.error("Error deleting query:", error);
      alert("Error deleting query, please try again!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/admin/query-master/${selectedQuery._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("token"),
          },
          body: JSON.stringify(formData),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to update query");
      }
      setShowModal(false);
      fetchQueries();
      alert("Query updated successfully");
    } catch (error) {
      console.error("Error updating query:", error);
      alert("Error updating query, please try again!");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "warning";
      case "in-progress":
        return "info";
      case "resolved":
        return "success";
      default:
        return "default";
    }
  };

  const filteredQueries = queries
    .filter((query) =>
      statusFilter === "all" ? true : query.status === statusFilter
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort by creation date, newest first

  return (
    <Grid
      item
      xs={10}
      style={{
        padding: "20px",
        height: "100%",
        fontFamily: "Noto Sans",
        fontWeight: "500",
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden", // Add this to prevent outer scrolling
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          overflowY: "auto", // Add this to enable scrolling within the component
          padding: "10px",
        }}
      >
        <Container fluid style={{ height: "100%" }}>
          <Row>
            <Col
              xs={12}
              style={{
                flex: 1,
                height: "90vh",
                width: "100%",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div style={{ width: "100%" }}>
                <Paper
                  elevation={3}
                  style={{
                    padding: "20px",
                    marginBottom: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <Typography variant="h5">Manage Student Queries</Typography>
                  <FormControl
                    sx={{
                      minWidth: 180,
                      "& .MuiInputLabel-root": {
                        backgroundColor: "#fff",
                        padding: "0 5px",
                        marginLeft: "-5px",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1976d2",
                      },
                    }}
                  >
                    <InputLabel>Filter Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      size="small"
                      sx={{ backgroundColor: "#fff" }}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="open" sx={{ color: "#e65100" }}>
                        Open
                      </MenuItem>
                      <MenuItem value="in-progress" sx={{ color: "#1976d2" }}>
                        In Progress
                      </MenuItem>
                      <MenuItem value="resolved" sx={{ color: "#2e7d32" }}>
                        Resolved
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Paper>

                {isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer component={Paper}>
                    {filteredQueries.length > 0 ? (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Student</TableCell>
                            <TableCell>Query</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Response</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredQueries.map((query) => (
                            <TableRow key={query._id}>
                              <TableCell>
                                {query.studentID?.firstName}{" "}
                                {query.studentID?.lastName}
                                <Typography variant="caption" display="block">
                                  {query.studentID?.email}
                                </Typography>
                              </TableCell>
                              <TableCell>{query.description}</TableCell>
                              <TableCell>
                                <Chip
                                  label={query.status}
                                  color={getStatusColor(query.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{query.response || "-"}</TableCell>
                              <TableCell>
                                {new Date(query.createdAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <EditNoteIcon
                                  onClick={() => handleEdit(query)}
                                  style={{
                                    color: "green",
                                    cursor: "pointer",
                                    marginRight: "10px",
                                  }}
                                />
                                <DeleteSweepIcon
                                  onClick={() => handleDelete(query._id)}
                                  style={{ color: "red", cursor: "pointer" }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div style={{ margin: "20px", textAlign: "center" }}>
                        No queries found.
                      </div>
                    )}
                  </TableContainer>
                )}

                {/* Material-UI Dialog instead of Bootstrap Modal */}
                <Dialog
                  open={showModal}
                  onClose={() => setShowModal(false)}
                  maxWidth="sm"
                  fullWidth
                  sx={{
                    "& .MuiDialog-container": {
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                    },
                    "& .MuiDialog-paper": {
                      backgroundColor: "#ffffff",
                      margin: "16px",
                      borderRadius: "12px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#1976d2",
                        fontWeight: 600,
                        mb: 3,
                        pb: 2,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      Update Query Status
                    </Typography>
                    {selectedQuery && (
                      <form onSubmit={handleSubmit}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          sx={{ mb: 3 }}
                        >
                          <InputLabel
                            id="status-label"
                            sx={{
                              color: "#1976d2",
                              "&.Mui-focused": {
                                color: "#1976d2",
                              },
                            }}
                          >
                            Status
                          </InputLabel>
                          <Select
                            labelId="status-label"
                            label="Status"
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            required
                            sx={{
                              backgroundColor: "#f5f5f5",
                              borderRadius: "8px",
                              "& .MuiSelect-select": {
                                color: "#2c3e50",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#1976d2",
                              },
                            }}
                          >
                            <MenuItem value="open" sx={{ color: "#e65100" }}>
                              Open
                            </MenuItem>
                            <MenuItem
                              value="in-progress"
                              sx={{ color: "#1976d2" }}
                            >
                              In Progress
                            </MenuItem>
                            <MenuItem
                              value="resolved"
                              sx={{ color: "#2e7d32" }}
                            >
                              Resolved
                            </MenuItem>
                          </Select>
                        </FormControl>

                        {formData.status !== "open" && (
                          <>
                            <FormControl fullWidth sx={{ mb: 3 }}>
                              <TextField
                                label="Response"
                                multiline
                                rows={4}
                                name="response"
                                value={formData.response}
                                onChange={handleInputChange}
                                required={formData.status === "resolved"}
                                sx={{
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "8px",
                                  "& .MuiInputBase-input": {
                                    color: "#2c3e50",
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: "#1976d2",
                                  },
                                }}
                              />
                            </FormControl>
                            <Button
                              variant="contained"
                              type="submit"
                              disabled={isLoading}
                              sx={{
                                backgroundColor: "#1976d2",
                                color: "#ffffff",
                                py: 1.5,
                                borderRadius: "8px",
                                textTransform: "none",
                                fontWeight: 600,
                                width: "100%",
                                "&:hover": {
                                  backgroundColor: "#1565c0",
                                },
                                "&:disabled": {
                                  backgroundColor: "#e0e0e0",
                                  color: "#9e9e9e",
                                },
                              }}
                            >
                              {isLoading ? (
                                <CircularProgress
                                  size={24}
                                  sx={{ color: "#ffffff" }}
                                />
                              ) : (
                                "Submit Response"
                              )}
                            </Button>
                          </>
                        )}
                      </form>
                    )}
                  </Box>
                </Dialog>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Grid>
  );
};

export default QueryManage;
